import React, { useCallback, useEffect, useMemo, useState } from "react";
import Table from "@components/table";
import deliveryManApi from "@apis/deliveryman";
import { Helper } from "@utils/index";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import styled from "@emotion/styled";
import {
  Package,
  CalendarDays,
  TrendingUp,
  Wallet,
  HandCoins,
  Banknote,
} from "lucide-react";

// Range presets understood by the backend's shared date-range resolver.
const RANGE_OPTIONS = [
  { value: "this_month", label: "This Month" },
  { value: "today", label: "Today" },
  { value: "this_week", label: "This Week" },
  { value: "this_year", label: "This Year" },
  { value: "all", label: "All Time" },
];

const Toolbar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;

const Select = styled.select`
  padding: 9px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  background: #fff;
  cursor: pointer;
  outline: none;
`;

const SummaryRow = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
`;

const StatCard = styled.div<{ accent: string }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: ${(p) => `${p.accent}10`};
  border: 1px solid ${(p) => `${p.accent}25`};
  border-radius: 12px;
  min-width: 180px;
`;

const StatIcon = styled.div<{ accent: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${(p) => `${p.accent}1f`};
  color: ${(p) => p.accent};
`;

const StatLabel = styled.span`
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
`;

const StatValue = styled.span`
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: #10b98115;
  color: #10b981;
  border: 1px solid #10b98130;
  border-radius: 100px;
  font-size: 13px;
  font-weight: 800;
`;

const PayBadge = styled.span<{ cod: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  background: ${(p) => (p.cod ? "#f59e0b15" : "#6366f115")};
  color: ${(p) => (p.cod ? "#f59e0b" : "#6366f1")};
  border: 1px solid ${(p) => (p.cod ? "#f59e0b30" : "#6366f130")};
`;

// Sub-table shown when a day row is expanded — lists the individual orders.
const SubTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #f8fafc;
  font-size: 13px;

  th {
    text-align: left;
    padding: 8px 14px;
    color: #64748b;
    font-weight: 700;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid #e2e8f0;
  }
  td {
    padding: 10px 14px;
    color: #334155;
    border-bottom: 1px solid #eef2f7;
  }
`;

const Money = styled.span<{ accent?: string }>`
  font-weight: 700;
  color: ${(p) => p.accent || "#0f172a"};
`;

const formatDate = (value: string) => {
  if (!value) return "---";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatTime = (value: string) => {
  if (!value) return "---";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "---";
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const DailyDeliveries = ({ state }: { state: any }) => {
  const { settings } = useGeneralSettings();
  const [range, setRange] = useState("this_month");
  const [data, setData] = useState<any>({
    total: 0,
    deliveryEarnings: 0,
    tips: 0,
    codCollected: 0,
    orderValue: 0,
    days: [],
  });
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!state?.id) return;
    try {
      setLoading(true);
      // "all" means no date filter — omit the range so the resolver returns all.
      const params = range && range !== "all" ? { range } : {};
      const res = await deliveryManApi.getDailyDelivered(state.id, params);
      setData({
        total: res?.total || 0,
        deliveryEarnings: res?.deliveryEarnings || 0,
        tips: res?.tips || 0,
        codCollected: res?.codCollected || 0,
        orderValue: res?.orderValue || 0,
        days: res?.days || [],
      });
    } catch (error) {
      console.error("Failed to fetch daily deliveries", error);
      setData({
        total: 0,
        deliveryEarnings: 0,
        tips: 0,
        codCollected: 0,
        orderValue: 0,
        days: [],
      });
    } finally {
      setLoading(false);
    }
  }, [range, state?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const days = data.days as any[];
  const activeDays = days.length;
  const avgPerDay = activeDays
    ? Math.round((data.total / activeDays) * 10) / 10
    : 0;

  const money = (val: number) => Helper.formatCurrency(val || 0, settings);

  const columns = useMemo(
    () => [
      {
        title: "Date",
        key: "date",
        width: "26%",
        render: (row: any) => (
          <span style={{ color: "#334155", fontWeight: 600, fontSize: "14px" }}>
            {formatDate(row.date)}
          </span>
        ),
      },
      {
        title: "Delivered",
        key: "count",
        width: "12%",
        render: (row: any) => (
          <CountBadge>
            <Package size={14} />
            {row.count}
          </CountBadge>
        ),
      },
      {
        title: "Delivery Earnings",
        key: "deliveryEarnings",
        width: "16%",
        render: (row: any) => (
          <Money accent="#10b981">{money(row.deliveryEarnings)}</Money>
        ),
      },
      {
        title: "Tips",
        key: "tips",
        width: "14%",
        render: (row: any) => (
          <Money accent="#f59e0b">{money(row.tips)}</Money>
        ),
      },
      {
        title: "COD Collected",
        key: "codCollected",
        width: "16%",
        render: (row: any) => (
          <Money accent="#ef4444">{money(row.codCollected)}</Money>
        ),
      },
      {
        title: "Order Value",
        key: "orderValue",
        width: "16%",
        render: (row: any) => <Money>{money(row.orderValue)}</Money>,
      },
    ],
    [settings],
  );

  // Expanded row: the individual orders delivered on that day.
  const expandedRowRender = (row: any) => {
    const list = (row.orders || []) as any[];
    if (!list.length) {
      return (
        <div style={{ padding: "12px 16px", color: "#94a3b8" }}>
          No order detail available.
        </div>
      );
    }
    return (
      <SubTable>
        <thead>
          <tr>
            <th>Order</th>
            <th>Customer</th>
            <th>Payment</th>
            <th>Delivery</th>
            <th>Tip</th>
            <th>Amount</th>
            <th>Delivered At</th>
          </tr>
        </thead>
        <tbody>
          {list.map((o) => (
            <tr key={o.id}>
              <td style={{ fontWeight: 700, color: "#475569" }}>
                #{o.trackingId || o.id}
              </td>
              <td>{o.customerName || "—"}</td>
              <td>
                <PayBadge cod={(o.paymentMethod || "").toLowerCase() === "cod"}>
                  {o.paymentMethod || "—"}
                </PayBadge>
              </td>
              <td>
                <Money accent="#10b981">{money(o.deliveryCharge)}</Money>
              </td>
              <td>
                <Money accent="#f59e0b">{money(o.tip)}</Money>
              </td>
              <td>
                <Money>{money(o.totalAmount)}</Money>
              </td>
              <td style={{ color: "#64748b" }}>{formatTime(o.deliveredAt)}</td>
            </tr>
          ))}
        </tbody>
      </SubTable>
    );
  };

  return (
    <div style={{ padding: "20px 0" }}>
      <Toolbar>
        <SummaryRow>
          <StatCard accent="#10b981">
            <StatIcon accent="#10b981">
              <Package size={20} />
            </StatIcon>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <StatLabel>Total Delivered</StatLabel>
              <StatValue>{data.total}</StatValue>
            </div>
          </StatCard>
          <StatCard accent="#3b82f6">
            <StatIcon accent="#3b82f6">
              <CalendarDays size={20} />
            </StatIcon>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <StatLabel>Active Days</StatLabel>
              <StatValue>{activeDays}</StatValue>
            </div>
          </StatCard>
          <StatCard accent="#8b5cf6">
            <StatIcon accent="#8b5cf6">
              <TrendingUp size={20} />
            </StatIcon>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <StatLabel>Avg / Active Day</StatLabel>
              <StatValue>{avgPerDay}</StatValue>
            </div>
          </StatCard>
          <StatCard accent="#10b981">
            <StatIcon accent="#10b981">
              <Wallet size={20} />
            </StatIcon>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <StatLabel>Delivery Earnings</StatLabel>
              <StatValue>{money(data.deliveryEarnings)}</StatValue>
            </div>
          </StatCard>
          <StatCard accent="#f59e0b">
            <StatIcon accent="#f59e0b">
              <HandCoins size={20} />
            </StatIcon>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <StatLabel>Tips</StatLabel>
              <StatValue>{money(data.tips)}</StatValue>
            </div>
          </StatCard>
          <StatCard accent="#ef4444">
            <StatIcon accent="#ef4444">
              <Banknote size={20} />
            </StatIcon>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <StatLabel>COD Collected</StatLabel>
              <StatValue>{money(data.codCollected)}</StatValue>
            </div>
          </StatCard>
        </SummaryRow>

        <Select value={range} onChange={(e) => setRange(e.target.value)}>
          {RANGE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </Toolbar>

      <Table
        columns={columns}
        data={days}
        loading={loading}
        hasSearchbar={false}
        expandedRowRender={expandedRowRender}
      />
    </div>
  );
};

export default DailyDeliveries;
